import { PageContainer } from "@/components/page-container";
import { TableContainer } from "@/components/table-container";
import { DynamicForm } from "@/components/dynamic-form";
import { useDynamicForm } from "@/components/dynamic-form/hook/useDynamicForm";
import { enhanceEndTime } from "@/components/dynamic-form/formatter/enhanceEndTime";
import { useGlobalStore } from "@/store/GlobalStore";
import { Table } from "@renderbus/ui";
import { columns } from "@/pages/export-list/config/table-columns";
import { getOperationType } from "@/components/operation-button/helper/getOperationType";
import { Operation } from "@/components/operation-button";
import { getInitialValues } from "@/components/dynamic-form/helper/getInitialValues";
import { useImmediateFetch } from "@/components/dynamic-form/hook/useImmediateFetch";
import { useWatchPagination } from "@/components/dynamic-form/hook/useWatchPagination";
import { useSearchConfig } from "@/pages/export-list/hooks/useSearchConfig";
import request from "project-config/services/request/request";
import { downloadFile } from "project-config/utils";
import { Modal } from "@/components/renderbus-modal";
import { WarningModalContent } from "@/components/warning-modal-content";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadRequest } from "@/utils/downloadRequest";

export function ExportList() {
  const { userInfo } = useGlobalStore();
  const { t } = useTranslation("center");

  const { form, tableData, fetchData, pageNum, setPageNum, total, pageSize } =
    useDynamicForm({
      api: "/api/rendering/task/common/getExportList",
      defaultPageSize: 10,
      formatter: {
        time: enhanceEndTime,
      },
    });

  const fetch = (defaultPageNum?: number) => {
    if (defaultPageNum) {
      setPageNum(defaultPageNum);
    }
    fetchData({
      extraData: {
        pageNum: defaultPageNum || pageNum,
        platform: userInfo?.platform ?? 2,
        type:
          form.getFieldsValue().type === undefined
            ? 0
            : form.getFieldsValue().type,
      },
    });
  };

  useImmediateFetch(fetch);
  useWatchPagination(pageSize, pageNum, fetch);

  const { searchConfig } = useSearchConfig();

  const [delId, setDelId] = useState<number>();
  const [delRowName, setDelRowName] = useState<string>();

  return (
    <>
      <PageContainer>
        <TableContainer>
          <DynamicForm
            initialValues={getInitialValues(searchConfig)}
            searchConfig={searchConfig}
            searchForm={form}
            onSearch={() => fetch(1)}
            showButtonIcon
          />

          <Table
            paginationCurrent={pageNum}
            columns={columns}
            dataSource={tableData}
            gap={false}
            total={total}
            defaultPageSize={pageSize}
            onRow={(row) => {
              return {
                onClick: async (e) => {
                  const type = getOperationType(e.target as HTMLDivElement);
                  if (type === Operation.Delete) {
                    setDelRowName(row.taskName);
                    setDelId(row.id);
                    return;
                  }

                  if (type === Operation.Download) {
                    downloadRequest("/api/rendering/task/common/getExcel", {
                      id: row.id,
                    }).then((res) => {
                      downloadFile(res as any);
                    });
                  }
                },
              };
            }}
            onPageNumChange={async (num) => {
              setPageNum(num);
            }}
          />
        </TableContainer>
      </PageContainer>
      <Modal
        open={!!delId}
        closable={false}
        onOk={async () => {
          const { code } = await request.post(
            "/api/rendering/task/common/delExportList",
            { id: delId }
          );
          if (code === 200) {
            setDelId(undefined);
            await fetch();
          }
        }}
        onCancel={() => setDelId(undefined)}
      >
        <WarningModalContent
          title={t("user_export_list_confirm_del_tips")}
          tip={t("user_export_list_confirm_del", { name: delRowName })}
        />
      </Modal>
    </>
  );
}
